import { merge } from 'ts-deepmerge';
import { useCallback, useEffect } from 'react';
import { NO_LEAD_AVAILABLE, NO_STATUS_AVAILABLE } from 'src/analytics/constants';
import {
    ZHLPersonalizedNextStepExperience,
    ZHLPersonalizedNextStepState,
    useGetViewSavedInformationModuleDataQuery,
} from 'src/services/graphql/generated/graphql';
import { MaybeLoanID } from 'src/common/types';
import { getHomeLoansDashboardInteractionEvent } from 'src/analytics/events/dashboard/getHomeLoansDashboardInteractionEvent';
import { getHomeLoansDashboardImpressionEvent } from 'src/analytics/events/dashboard/getHomeLoansDashboardImpressionEvent';
import {
    HOME_LOANS_DASHBOARD_IMPRESSION_EVENTS,
    HOME_LOANS_DASHBOARD_INTERACTION_EVENTS,
} from 'src/analytics/events/registeredEvents';
import { trackEvent } from 'src/analytics/events/trackEvent';
import { useUnauthorizedAccessGuard } from 'src/services/graphql/hooks/useUnauthorizedAccessGuard';
import { useFirstPartyEligibility } from 'src/experiences/MortgageDashboard/hooks/useFirstPartyEligibility/useFirstPartyEligibility';
import { ServerSideModuleData } from '../../GetServerSideProps/utils/getServerSideModuleData';
import { isUUID } from '../../../../utils/validators/isUUID';
import { useStartBlend } from '../../hooks/useStartBlend/useStartBlend';

export type ViewSavedInformationState = {
    sendToApplication: () => void;
    trackCTAClick: () => void;
    status: ZHLPersonalizedNextStepState | null;
    experience: ZHLPersonalizedNextStepExperience | null;
};

const isServerSideDataSufficient = (data: Pick<ServerSideModuleData, 'ViewSavedInformation'>) => {
    const { homeLoansDashboardByLoanId } = data.ViewSavedInformation;
    const { personalizedNextSteps } = homeLoansDashboardByLoanId ?? {};
    const { currentState: status = null } = personalizedNextSteps ?? {};
    return !!status;
};

export const useViewSavedInformationState = ({
    loanID,
    serverFetchedData,
}: {
    loanID: MaybeLoanID;
    serverFetchedData: Pick<ServerSideModuleData, 'ViewSavedInformation'>;
}): ViewSavedInformationState => {
    const { ViewSavedInformation } = serverFetchedData;
    const isLoanIDInvalid = !isUUID(loanID);
    const { loading, data, error } = useGetViewSavedInformationModuleDataQuery({
        variables: { id: loanID as string },
        skip: isLoanIDInvalid || isServerSideDataSufficient(serverFetchedData),
    });
    useUnauthorizedAccessGuard(error);
    const mergedData = merge(data ?? {}, ViewSavedInformation);
    const { homeLoansDashboardByLoanId } = mergedData;
    const { currentlySelectedLoan, personalizedNextSteps } = homeLoansDashboardByLoanId ?? {};
    const { lead, currentlyLoggedInBorrower } = currentlySelectedLoan ?? {};
    const { id: leadID = null } = lead ?? {};
    const { id: borrower_id = null } = currentlyLoggedInBorrower ?? {};
    const { currentState: status = null, currentExperience = null } = personalizedNextSteps ?? {};
    const { isFirstPartyExperience } = useFirstPartyEligibility(status, currentExperience);

    useEffect(() => {
        if (loading) {
            return;
        }
        if (isFirstPartyExperience) {
            return;
        }
        trackEvent(
            getHomeLoansDashboardImpressionEvent({
                event: HOME_LOANS_DASHBOARD_IMPRESSION_EVENTS.VIEW_SAVED_INFORMATION_CARD_MODULE_LOADED,
                journey_stage_cd: status ?? NO_STATUS_AVAILABLE,
                lead_id: leadID ?? NO_LEAD_AVAILABLE,
            })
        );
    }, [leadID, status, loading, isFirstPartyExperience]);

    const { sendToBlend } = useStartBlend(loanID, borrower_id);

    const trackCTAClick = useCallback(() => {
        if (!status) {
            return;
        }
        trackEvent(
            getHomeLoansDashboardInteractionEvent({
                event: HOME_LOANS_DASHBOARD_INTERACTION_EVENTS.SAVED_INFORMATION_PRIMARY_CTA_CLICK,
                journey_stage_cd: status,
                lead_id: leadID ?? NO_LEAD_AVAILABLE,
            })
        );
    }, [leadID, status]);
    return {
        sendToApplication: sendToBlend,
        trackCTAClick,
        status,
        experience: currentExperience,
    };
};
